import * as React from "react";

import { applySort, nextSortState, type SortState, type SortValue } from "./tableSort";

/**
 * React binding for the {@link applySort} primitive (CHP-417).
 *
 * Holds tri-state sort state for one ledger, exposes a `toggle(column)` that
 * advances asc → desc → reset, and returns the sorted rows. When unsorted it
 * returns the input `rows` reference untouched, so the caller's default order
 * (e.g. CHP-364 status grouping) shows through.
 *
 * CHP-1038: `initialSort` seeds the mount-time state (e.g. a scorecard
 * click-through landing pre-sorted). Mount-time only — to re-apply a new
 * preset, remount the consumer (key on the preset).
 */
export function useTableSort<Row, ColumnKey extends string>(
  rows: readonly Row[],
  getValue: (row: Row, column: ColumnKey) => SortValue,
  initialSort?: SortState<ColumnKey>,
): {
  sort: SortState<ColumnKey>;
  toggle: (column: ColumnKey) => void;
  sortedRows: Row[];
} {
  const [sort, setSort] = React.useState<SortState<ColumnKey>>(
    initialSort ?? { column: null, direction: null },
  );

  const toggle = React.useCallback((column: ColumnKey) => {
    setSort((current) => nextSortState(current, column));
  }, []);

  // `getValue` is referenced by identity; ledgers pass a stable module-level fn.
  const sortedRows = React.useMemo(() => applySort(rows, sort, getValue), [rows, sort, getValue]);

  return { sort, toggle, sortedRows };
}
